import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { Logo } from "../components/AppShell";
import { Button } from "../components/ui/button";
import { ArrowRight, Search, Scale, ShieldCheck, GitCompareArrows, FileSearch, LineChart } from "lucide-react";
import { useAuth } from "../context/AuthContext";

const steps = [
  { icon: FileSearch, t: "Your business & decision", d: "Describe your business once, then enter any decision you're weighing." },
  { icon: Search, t: "Find real analogs", d: "We surface businesses that made structurally similar decisions — not the same sentence." },
  { icon: LineChart, t: "Analyze outcomes", d: "What was their situation before, what changed, and what happened after." },
  { icon: GitCompareArrows, t: "Compare with you", d: "A transparent similarity score across business, customer, decision & market." },
  { icon: Scale, t: "Evidence, not prediction", d: "Positive / negative / mixed cases — with sources. Never a fake yes/no." },
  { icon: ShieldCheck, t: "Know the unknowns", d: "We label FACT vs AI INFERENCE vs UNKNOWN and flag weak evidence honestly." },
];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const go = () => navigate(user ? "/dashboard" : "/auth");

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white/70 backdrop-blur-xl border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <Logo />
          <div className="flex items-center gap-3">
            <Link to="/auth" data-testid="signin-link" className="text-sm font-medium text-slate-600 hover:text-slate-900">Sign in</Link>
            <Button size="sm" onClick={go} data-testid="header-cta" className="bg-slate-900 hover:bg-slate-800 rounded-lg">Get started</Button>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 grid-bg opacity-60" />
        <div className="relative max-w-4xl mx-auto px-6 pt-24 pb-20 text-center">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-slate-200 bg-white text-xs font-mono text-slate-600 mb-8">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" /> EVIDENCE-FIRST DECISION RESEARCH
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-slate-900">
            Make decisions with evidence from businesses that already made them.
          </h1>
          <p className="text-lg text-slate-600 leading-relaxed mt-6 max-w-2xl mx-auto">
            Decision Analog finds comparable real-world business decisions, shows what happened afterward, and explains how their situation compares with yours.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <Button size="lg" onClick={go} data-testid="hero-cta"
              className="bg-slate-900 hover:bg-slate-800 rounded-lg h-12 px-6 text-base gap-2">
              Analyze My First Decision <ArrowRight className="w-4 h-4" />
            </Button>
            <span className="text-sm text-slate-400 font-mono">Includes a live demo analysis</span>
          </div>
        </div>
      </section>

      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 shadow-sm mb-16">
          <p className="font-mono text-xs uppercase tracking-widest text-slate-400 mb-3">The core idea</p>
          <p className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900 leading-tight">
            Not “AI predicts your future.” <span className="text-slate-400">Instead:</span> the closest real precedents, and what actually happened when businesses like yours made a similar move.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {steps.map((s, i) => (
            <div key={i} className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
              <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center mb-4">
                <s.icon className="w-5 h-5 text-slate-900" />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg mb-1.5">{s.t}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-slate-200 py-8">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between text-sm text-slate-400">
          <Logo />
          <span className="font-mono text-xs">Decision research • not financial advice</span>
        </div>
      </footer>
    </div>
  );
}
